import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import { TooltipProps } from '@mui/material/Tooltip';
import { Marker as DefaultMarker } from '@react-google-maps/api';
import { SvgIcon } from 'components';

import { MarkerTooltipOverlay } from './MarkerTooltipOverlay';

import { TOOLTIP_APPEAR_DELAY } from 'constants/common';

export enum MarkerIconType {
  Plant = 'plant',
  PlantHover = 'plantHover',
  PlantSatellite = 'plantSatellite',
  PlantSatelliteHover = 'plantSatelliteHover',
  Site = 'site',
  SiteHover = 'siteHover',
  SiteSatellite = 'siteSatellite',
  SiteSatelliteHover = 'siteSatelliteHover',
  Truck = 'truck',
  TruckHover = 'truckHover',
  TruckSatellite = 'truckSatellite',
  TruckSatelliteHover = 'truckSatelliteHover',
  ReturningTruck = 'returningTruck',
  ReturningTruckHover = 'returningTruckHover',
  ReturningTruckSatellite = 'returningTruckSatellite',
  ReturningTruckSatelliteHover = 'returningTruckSatelliteHover',
}

const Wrapper = styled.div<{
  markerOpacity: number;
  isOnline?: boolean;
  icon: MarkerIconType;
  isDraggable?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  border-radius: 100%;
  user-select: none;
  transform: translate(
    -50%,
    ${props =>
      [MarkerIconType.Site, MarkerIconType.SiteSatellite].includes(props.icon)
        ? '-100%'
        : '-50%'}
  );
  opacity: ${props => props.markerOpacity};
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  svg {
    color: ${props =>
      !props.isOnline
        ? props.theme.custom.palette.muted500
        : props.theme.custom.palette.secondary600};
  }
  .svg-icon {
    &:nth-child(2) {
      display: none;
    }
  }
  ${props =>
    !props.isDraggable
      ? `&:hover {
    .svg-icon {
      &:nth-child(1) {
        display: none;
      }
      &:nth-child(2) {
        display: initial;
      }
    }
  }`
      : ''}
`;

const StyledLabel = styled.div<{ isOnline?: boolean }>`
  border: 2px solid
    ${props =>
      !props.isOnline
        ? props.theme.custom.palette.muted500
        : props.theme.custom.palette.secondary600};
  color: ${props =>
    !props.isOnline
      ? props.theme.custom.palette.muted800
      : props.theme.custom.palette.darkText};
  border-radius: 7px;
  position: absolute;
  background: white;
  max-width: 10rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0.4rem;
`;

type Props = {
  id: string;
  lat?: number | null;
  lng?: number | null;
  label?: string | ReactElement;
  icon: MarkerIconType;
  iconHover?: MarkerIconType;
  tooltip?: string | ReactElement | null;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onDragEnd?: ({ lat, lng }: { lat: number; lng: number }) => void;
  isDraggable?: boolean;
  width: string;
  height: string;
  markerOpacity?: number;
  zIndex?: number;
  className?: string;
  isOnline?: boolean;
  tooltipProps?: Partial<TooltipProps>;
};

export const Marker = React.memo(
  ({
    id,
    lat,
    lng,
    onClick,
    onMouseEnter,
    onDragEnd,
    icon,
    iconHover,
    tooltip,
    label,
    width,
    height,
    markerOpacity = 1,
    isOnline,
    className,
    tooltipProps,
    isDraggable,
    zIndex,
  }: Props) => {
    // needed for smooth marker dragging
    const [draggablePosition, setDraggablePosition] = useState<{
      lat: number;
      lng: number;
    } | null>(null);
    // Marker dragging and hover functionality is needed because it doesn't have its own js/css functionality
    const [isMarkerDragging, setIsMarkerDragging] = useState<boolean>(false);
    const [isMarkerHover, setIsMarkerHover] = useState<boolean>(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

    const position = useMemo(
      () => ({
        lat: lat || 0,
        lng: lng || 0,
      }),
      [lat, lng],
    );

    useEffect(() => {
      if (!isDraggable) {
        // reset draggablePosition to initial if isDraggable = false
        setDraggablePosition(null);
      }
    }, [isDraggable]);

    const clearTooltipTimer = useCallback(() => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
      setShowTooltip(false);
    }, []);

    const startTooltipTimer = useCallback(() => {
      if (!isMarkerDragging && tooltip) {
        tooltipTimerRef.current = setTimeout(() => {
          setShowTooltip(true);
        }, TOOLTIP_APPEAR_DELAY);
      }
    }, [isMarkerDragging, tooltip]);

    useEffect(() => {
      return () => {
        if (tooltipTimerRef.current) {
          clearTimeout(tooltipTimerRef.current);
        }
      };
    }, []);

    const onDragMarker = useCallback((event: google.maps.MapMouseEvent) => {
      const newLat = event.latLng?.lat();
      const newLng = event.latLng?.lng();
      if (newLat && newLng) {
        setDraggablePosition({
          lat: newLat,
          lng: newLng,
        });
      }
    }, []);

    const onDragEndMarker = useCallback(
      (event: google.maps.MapMouseEvent) => {
        const newLat = event.latLng?.lat();
        const newLng = event.latLng?.lng();
        if (newLat && newLng) {
          setDraggablePosition({
            lat: newLat,
            lng: newLng,
          });

          onDragEnd?.({ lng: newLng, lat: newLat });
        }
        setIsMarkerDragging(false);
      },
      [onDragEnd],
    );

    const marker = (
      <Wrapper
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        markerOpacity={markerOpacity}
        isOnline={isOnline}
        icon={icon}
        isDraggable={isDraggable}
      >
        {!isMarkerDragging && !isMarkerHover && (
          <SvgIcon width={width} height={height} icon={icon} />
        )}
        <SvgIcon width={width} height={height} icon={iconHover || icon} />
        {label && <StyledLabel isOnline={isOnline}>{label}</StyledLabel>}
        {isDraggable && (
          <DefaultMarker
            position={draggablePosition || position}
            draggable={true}
            onDrag={onDragMarker}
            opacity={0}
            clickable={true}
            onDragStart={() => {
              setIsMarkerDragging(true);
              clearTooltipTimer();
            }}
            onDragEnd={onDragEndMarker}
            zIndex={100}
            onMouseOver={() => {
              setIsMarkerHover(true);
              startTooltipTimer();
            }}
            onMouseOut={() => {
              setIsMarkerHover(false);
              clearTooltipTimer();
            }}
            options={{
              cursor: 'pointer',
              clickable: true,
            }}
          />
        )}
      </Wrapper>
    );

    return (
      <MarkerTooltipOverlay
        id={id}
        position={isDraggable ? draggablePosition || position : position}
        tooltip={tooltip}
        open={isDraggable ? showTooltip : !!tooltipProps?.open}
        className={className}
        tooltipProps={tooltipProps}
        zIndex={zIndex || 10}
      >
        {marker}
      </MarkerTooltipOverlay>
    );
  },
);
